/**
 * Generates direction cone images (ring-sector / annular wedge) pointing north.
 * The image is anchored at bottom-center so Mapbox's `icon-rotate` can spin it
 * clockwise to match the compass heading.
 */

const WIDTH = 64;
const HEIGHT = 96;
const CENTER_Y_INSET = 1; // keep arc math stable, avoid clipping at bottom edge
const HALF_ANGLE_DEG = 30; // outer arc spread
const INNER_RADIUS = 13; // distance from center to inner edge
const OUTER_RADIUS = 40; // distance from center to outer edge

function degToRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

function arcPoints(
  cx: number,
  cy: number,
  r: number,
  startRad: number,
  endRad: number,
  segments: number,
): Array<{ x: number; y: number }> {
  const pts: Array<{ x: number; y: number }> = [];
  for (let i = 0; i <= segments; i++) {
    const t = i / segments;
    const a = startRad + (endRad - startRad) * t;
    pts.push({ x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) });
  }
  return pts;
}

function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

const cache = new Map<string, ImageData>();

function generateCone(color: string): ImageData {
  if (cache.has(color)) return cache.get(color)!;

  const canvas = document.createElement('canvas');
  canvas.width = WIDTH;
  canvas.height = HEIGHT;
  const ctx = canvas.getContext('2d')!;

  const cx = WIDTH / 2;
  const cy = HEIGHT - CENTER_Y_INSET;

  const startRad = degToRad(-90 - HALF_ANGLE_DEG);
  const endRad = degToRad(-90 + HALF_ANGLE_DEG);
  const segments = 24;

  // Build ring-sector path (outer arc → inner arc reversed → close)
  const outer = arcPoints(cx, cy, OUTER_RADIUS, startRad, endRad, segments);
  const inner = arcPoints(cx, cy, INNER_RADIUS, endRad, startRad, segments);

  ctx.beginPath();
  ctx.moveTo(outer[0].x, outer[0].y);
  for (let i = 1; i < outer.length; i++) {
    ctx.lineTo(outer[i].x, outer[i].y);
  }
  for (let i = 0; i < inner.length; i++) {
    ctx.lineTo(inner[i].x, inner[i].y);
  }
  ctx.closePath();

  // Radial gradient: opaque at inner edge, transparent at outer edge
  const gradient = ctx.createRadialGradient(cx, cy, INNER_RADIUS, cx, cy, OUTER_RADIUS);
  gradient.addColorStop(0, color);
  gradient.addColorStop(1, hexToRgba(color, 0));

  ctx.fillStyle = gradient;
  ctx.fill();

  const imageData = ctx.getImageData(0, 0, WIDTH, HEIGHT);
  cache.set(color, imageData);
  return imageData;
}

export const CONE_COLOR_DEFAULT = '#f97316'; // orange, matches field note marker stroke
export const CONE_COLOR_HOVER = '#22c55e'; // green, matches hovered marker stroke

export function generateConeImage(): ImageData {
  return generateCone(CONE_COLOR_DEFAULT);
}

export function generateConeImageHover(): ImageData {
  return generateCone(CONE_COLOR_HOVER);
}
